import { Typography } from '../../../../../shared/ui/typography/typography';
import { Button } from '@vtbhome/juicyfront';
import React from 'react';
import { DeclineModalBody, DeclineModalFooter } from './modal-confirm.styles';
import { ModalConfirmProps } from './types';
import { Field, Form } from 'react-final-form';
import { Textarea } from '../../../../../shared/ui/textarea/textarea';
import { CardDeclineRequest } from '../../../../../shared/ui/card-decline-request/card-decline-request';
import { useRemoveUserRequestFromOrderMutation } from '../../api/decline-employee-request-in-order-api';
import { CACHE_KEY_DECLINE_REQUEST_IN_ORDER } from '../../config/api-constants';
import { DeclineFormKeys } from '../../config/form-decline-constants';
import { validate } from '../../lib/form-decline-validate';
import toast from 'react-hot-toast';
import { Toast } from '@shared/ui/toast/toast';
import { ROUTE_ADMIN_PRODUCT_BASE } from '@shared/config/routes/routes';
import { Modal } from '@shared/ui/modal/modal';
import { useRedirect } from '@shared/lib/hooks/use-redirect/use-redirect';
import { usePrefetchProduct } from '@entities/product/api/product-api';
import { usePrefetchOrder } from '@entities/order/api/order-api';
import { listOrdersInProductParameters } from '@shared/lib/utils/list-orders-in-product-parameters/list-orders-in-product-parameters';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';

export const ModalConfirm: React.FC<ModalConfirmProps> = ({
    isShow,
    request,
    order,
    isLastRequest,
    onClose,
    onAccept,
}) => {
    const user = useCurrentUserSelector();
    const redirect = useRedirect();

    const [rejectRequest, rejectRequestResult] = useRemoveUserRequestFromOrderMutation({
        fixedCacheKey: CACHE_KEY_DECLINE_REQUEST_IN_ORDER,
    });
    const isLoading = rejectRequestResult.isLoading;

    const prefetchProduct = usePrefetchProduct('productShortInfo', { ifOlderThan: 300 });
    const prefetchOrder = usePrefetchOrder('ordersList', { ifOlderThan: 300 });

    const handlePrefetch = () => {
        const productId = order.product.id;
        prefetchProduct(productId);
        prefetchOrder(
            listOrdersInProductParameters({
                productId,
                user,
            })
        );
    };

    const handleSubmit = async (fields: Record<DeclineFormKeys, string>) => {
        await rejectRequest({
            data: {
                id: request.id,
                comment: fields[DeclineFormKeys.Comment],
            },
            optimisticData: {
                requestId: request.id,
                orderId: order.id,
            },
        });
        if (isLastRequest) {
            toast.custom((t) => {
                return (
                    <Toast
                        type='success'
                        title='Успешно!'
                        description='Сделка удалена'
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            redirect({ url: `${ROUTE_ADMIN_PRODUCT_BASE}/${order.product.id}` });
        } else {
            onAccept();
        }
    };

    return (
        <Form
            subscription={{}}
            onSubmit={handleSubmit}
            validate={validate}
            render={(form) => (
                <Modal
                    isShow={isShow}
                    size='l'
                    showLine={false}
                    header={<Typography size='h5'>Отклонение запроса</Typography>}
                    footer={
                        <DeclineModalFooter>
                            <Button disabled={isLoading} type='button' buttonType='light' size='l' onClick={onClose}>
                                Отменить
                            </Button>
                            <Button
                                disabled={isLoading}
                                preloader={isLoading}
                                type='submit'
                                buttonType='primary'
                                size='l'
                                onClick={form.handleSubmit}>
                                Подтвердить
                            </Button>
                        </DeclineModalFooter>
                    }
                    onClose={onClose}>
                    <DeclineModalBody>
                        <Typography size='body1_long'>
                            {isLastRequest && (
                                <b>{`После удаления единственного запроса сделка будет удалена автоматически.\n`}</b>
                            )}
                            Вы действительно хотите отклонить регистрацию на этот продукт?
                        </Typography>
                        <CardDeclineRequest
                            request={request}
                            product={order.product}
                            user={request.user}
                            onPrefetchProduct={handlePrefetch}
                        />
                        <Field
                            name={DeclineFormKeys.Comment}
                            render={({ input, meta }) => (
                                <Textarea
                                    label='Комментарий (необязательно)'
                                    placeholder='Введите комментарий'
                                    name={input.name}
                                    value={input.value}
                                    error={meta.touched && meta.error}
                                    onChange={input.onChange}
                                />
                            )}
                        />
                    </DeclineModalBody>
                </Modal>
            )}
        />
    );
};
